import { useRef, useState } from "react";
import type { MediaAsset } from "../domain/types";
import {
  MediaValidationError,
  createAndUploadMedia,
  formatBytes,
} from "../media/createMediaAsset";
import { ID_ACCEPT } from "../media/limits";
import { uploadStatusLabel, uploadUserMessage } from "../media/uploadMessages";
import { useT } from "../i18n";
import { LiveStillCapture } from "./LiveStillCapture";

export function IdCapturePanel({
  asset,
  userId,
  onChange,
}: {
  asset: MediaAsset | null;
  userId: string;
  onChange: (asset: MediaAsset | null) => void;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (!userId) {
      setError(t("capture.id.signIn"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const uploaded = await createAndUploadMedia("government_id", file, userId);
      onChange(uploaded);
      const msg = uploadUserMessage(uploaded, t);
      if (uploaded.uploadStatus === "failed") {
        setError(msg || t("capture.id.uploadFailed"));
      } else if (msg) {
        setError(msg);
      }
    } catch (e) {
      setError(e instanceof MediaValidationError ? e.message : t("capture.id.badFile"));
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = asset?.publicUrl || asset?.objectUrl || "";
  const isPdf = asset?.mimeType === "application/pdf" || /\.pdf$/i.test(asset?.fileName ?? "");
  const uploadLabel = uploadStatusLabel(asset, t);

  return (
    <div className={`capture${asset ? " is-ready" : ""}`}>
      <div
        className={`capture__frame capture__frame--media${asset ? "" : " is-empty"}`}
        aria-hidden={!asset}
      >
        {asset && !isPdf && previewUrl ? (
          <img
            src={previewUrl}
            alt={t("capture.id.previewAlt")}
            className="capture__preview"
          />
        ) : null}
        {asset && isPdf ? (
          <div className="capture__pdf-label" aria-label={t("capture.id.pdfAria")}>
            PDF
          </div>
        ) : null}
      </div>

      <div className="capture__title">
        {asset ? t("capture.id.titleReady") : t("capture.id.titleEmpty")}
      </div>
      <p className="capture__hint">
        {asset
          ? t("capture.id.hintReady", {
              fileName: asset.fileName,
              bytes: formatBytes(asset.sizeBytes),
              saved: asset.storageKey ? t("capture.id.savedSuffix") : "",
            })
          : t("capture.id.hintEmpty")}
      </p>

      {asset ? (
        <span
          className="status-chip"
          title={asset.storageKey ?? t("capture.uploadStatus.awaitingUpload")}
        >
          {uploadLabel}
        </span>
      ) : null}

      {error ? (
        <p
          className="capture__error"
          style={
            asset?.uploadSource === "local-fallback" && asset.uploadStatus === "uploaded"
              ? { color: "var(--ink-muted)" }
              : undefined
          }
        >
          {error}
        </p>
      ) : null}

      <div className="capture__actions">
        <button
          type="button"
          className="capture__btn"
          disabled={busy}
          onClick={() => setCameraOpen(true)}
        >
          {busy
            ? t("capture.id.uploading")
            : asset
              ? t("capture.id.retake")
              : t("capture.id.takePhoto")}
        </button>
        <button
          type="button"
          className="capture__btn capture__btn--ghost"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {asset ? t("capture.id.replaceFile") : t("capture.id.chooseFile")}
        </button>
        {asset ? (
          <button
            type="button"
            className="capture__btn capture__btn--danger"
            disabled={busy}
            onClick={() => onChange(null)}
          >
            {t("capture.id.remove")}
          </button>
        ) : null}
      </div>

      <LiveStillCapture
        open={cameraOpen}
        facing="environment"
        title={t("capture.id.cameraTitle")}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => void onFile(file)}
      />

      <input
        ref={fileRef}
        className="sr-only"
        type="file"
        accept={ID_ACCEPT}
        onChange={(e) => {
          void onFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </div>
  );
}
